import { useCan } from '@/components/can/Can';
import { defaultMeta } from '@/constants/defaultMeta';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { parseDateRangeSearch } from '@/lib/parseDateRange';
import { getSortingStateParser } from '@/lib/parsers';
import { sortBy } from '@/lib/sortBy';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { referralReportQueries } from '@/services/dashboard/referralReports/referralReports.queries';
import { ReferralUserType } from '@/services/dashboard/referralReports/referralReports.types';
import { useSearch } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import type { CustomerReferralRow } from '../mapCustomerReferralReports';

export type CustomerReferralStage2Row = CustomerReferralRow;

export const useCustomerReferralStage2Report = () => {
  const { locale } = useDynamicLocale();
  const canReadReport = useCan({ allOf: [PERMS.referral.customerReport.read] });
  const { q, createdDate, page, fetch, userType } = useSearch({
    from: '/(app)/dashboard/referral-reports/',
  });
  const [sort] = useQueryState('sort', getSortingStateParser<CustomerReferralStage2Row>().withDefault([]));
  const dateRange = parseDateRangeSearch(Array.isArray(createdDate) ? createdDate.join(',') : createdDate);
  const { data, isLoading } = useQuery({
    ...referralReportQueries.customerList({
      search: q,
      stageCode: 'stage_2',
      fromDateMs: dateRange.start,
      toDateMs: dateRange.end,
      row: page,
      fetch,
    }),
    enabled: userType !== ReferralUserType.Affiliate && canReadReport,
  });

  const rows = sortBy(
    (data?.data ?? []).map(
      (report, index): CustomerReferralStage2Row => ({
        referralId: `${report.referrerCustomerId}-${report.referredCustomerId}-${report.reservationDateTimeMs}-${report.rewardTransactionDateTimeMs}-${index}`,
        referrerId: report.referrerCustomerId,
        referrerName: report.referrerCustomerFullname?.trim() || report.referrerCustomerNickname?.trim() || '-',
        referrerNickname: report.referrerCustomerNickname?.trim() || '-',
        reservedBy: report.referredCustomerFullname?.trim() || '-',
        reservedByNickname: report.referredCustomerNickname?.trim() || '-',
        reservationDateTime: report.reservationDateTimeMs,
        pointsEarned: report.rewardAmount,
        stageDetails:
          locale === 'ja'
            ? report.stageDetailJp || report.stageDetail || '-'
            : report.stageDetail || report.stageDetailJp || '-',
        receiptDateTime: report.rewardTransactionDateTimeMs,
      }),
    ),
    sort,
    { createdDate: 'receiptDateTime' },
  );

  return {
    data: rows,
    isLoading,
    pagination: data?.meta?.pagination ?? defaultMeta,
  };
};
